import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShareNetwork, FilePdf, WarningCircle, ArrowElbowDownRight, CheckCircle, LinkSimple } from "@phosphor-icons/react";
import { api, apiError, fmtMoney, downloadFile } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ShareLinkDialog from "@/components/ShareLinkDialog";
import TaskDetailDialog from "@/components/TaskDetailDialog";
import ProjectScheduleTab from "@/components/project/ProjectScheduleTab";
import ProjectScurveTab from "@/components/project/ProjectScurveTab";
import ProjectCostsTab from "@/components/project/ProjectCostsTab";
import ProjectPhotosTab from "@/components/project/ProjectPhotosTab";

export default function ProjectDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [statusSummary, setStatusSummary] = useState(null);
  const [resourcesCatalog, setResourcesCatalog] = useState([]);
  const [detailTask, setDetailTask] = useState(null);
  const [sharing, setSharing] = useState(false);

  const canWrite = ["admin", "manager", "supervisor"].includes(user?.role);
  const canManage = ["admin", "manager"].includes(user?.role);

  const load = useCallback(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data));
    api.get(`/projects/${id}/tasks`).then((r) => setTasks(r.data));
    api.get(`/projects/${id}/status-summary`).then((r) => setStatusSummary(r.data)).catch(() => {});
    api.get(`/resources`).then((r) => setResourcesCatalog(r.data || [])).catch(() => setResourcesCatalog([]));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const doExport = async (kind, fmt, orient) => {
    try {
      const qs = new URLSearchParams({ fmt });
      if (orient) qs.set("orient", orient);
      await downloadFile(`/projects/${id}/exports/${kind}?${qs.toString()}`, `${kind}_${project?.code || id}.${fmt}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const shareCompletePdf = async (orient = "landscape") => {
    setSharing(true);
    try {
      const res = await api.get(`/projects/${id}/exports/complete?fmt=pdf&orient=${orient}`, { responseType: "blob" });
      const filename = `informe_completo_${project?.code || id}.pdf`;
      const file = new File([res.data], filename, { type: "application/pdf" });
      const shareData = {
        title: `UG Managment by OOMINING — ${project?.name}`,
        text: `Informe integral del proyecto ${project?.code}: Gantt, Curva S, costos y anexo fotográfico.`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData) && navigator.share) {
        await navigator.share(shareData);
      } else {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        toast.info(t("shareFallback"));
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(apiError(e));
    } finally { setSharing(false); }
  };

  if (!project) return <p className="text-slate-500">{t("loading")}</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-label">{project.code} · {project.area || t("generalArea")}</p>
          <h1 className="font-heading font-black text-4xl tracking-tight text-slate-900 mt-1" data-testid="project-detail-title">{project.name}</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">{project.description}</p>
          <div className="flex gap-8 mt-4">
            <div><p className="overline-label">{t("progress")}</p><p className="font-heading font-bold text-xl">{project.progress}%</p></div>
            <div><p className="overline-label">{t("budget")}</p><p className="font-heading font-bold text-xl">{fmtMoney(project.budget)}</p></div>
            <div><p className="overline-label">{t("startDate")}</p><p className="font-heading font-bold text-xl">{project.start_date}</p></div>
            <div><p className="overline-label">{t("endDate")}</p><p className="font-heading font-bold text-xl">{project.end_date}</p></div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="share-complete-pdf" disabled={sharing} className="bg-blue-900 hover:bg-blue-800 gap-2">
                <ShareNetwork size={16} weight="bold" /> {sharing ? t("generating") : t("shareCompletePdf")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white" align="end">
              <DropdownMenuItem data-testid="share-complete-pdf-landscape" onClick={() => shareCompletePdf("landscape")}>{t("orientLandscape")}</DropdownMenuItem>
              <DropdownMenuItem data-testid="share-complete-pdf-portrait" onClick={() => shareCompletePdf("portrait")}>{t("orientPortrait")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="download-complete-pdf" className="gap-2">
                <FilePdf size={16} weight="bold" /> {t("downloadCompletePdf")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white" align="end">
              <DropdownMenuItem data-testid="download-complete-pdf-landscape" onClick={() => doExport("complete", "pdf", "landscape")}>{t("orientLandscape")}</DropdownMenuItem>
              <DropdownMenuItem data-testid="download-complete-pdf-portrait" onClick={() => doExport("complete", "pdf", "portrait")}>{t("orientPortrait")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canManage && (
            <ShareLinkDialog
              projectId={id}
              projectCode={project.code}
              trigger={
                <Button variant="outline" data-testid="open-share-link-dialog" className="gap-2 border-blue-300 text-blue-900 hover:bg-blue-50">
                  <LinkSimple size={16} weight="bold" /> {t("shareLinkButton")}
                </Button>
              }
            />
          )}
        </div>
      </div>

      {statusSummary && (statusSummary.counts.delayed > 0 || statusSummary.counts.at_risk > 0 || statusSummary.counts.early > 0 || tasks.length > 0) && (
        <StatusSummaryCard summary={statusSummary} t={t} />
      )}

      <Tabs defaultValue="schedule">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="schedule" data-testid="tab-schedule">{t("schedule")}</TabsTrigger>
          <TabsTrigger value="scurve" data-testid="tab-scurve">{t("sCurve")}</TabsTrigger>
          <TabsTrigger value="costs" data-testid="tab-costs">{t("costs")}</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos">{t("photos")}</TabsTrigger>
        </TabsList>

        <TabsContent value="schedule" className="mt-4">
          <ProjectScheduleTab
            projectId={id}
            project={project}
            tasks={tasks}
            resourcesCatalog={resourcesCatalog}
            canWrite={canWrite}
            canManage={canManage}
            onTasksChanged={load}
            setDetailTask={setDetailTask}
            doExport={doExport}
          />
        </TabsContent>

        <TabsContent value="scurve" className="mt-4">
          <ProjectScurveTab projectId={id} doExport={doExport} />
        </TabsContent>

        <TabsContent value="costs" className="mt-4">
          <ProjectCostsTab projectId={id} canManage={canManage} doExport={doExport} />
        </TabsContent>

        <TabsContent value="photos" className="mt-4">
          <ProjectPhotosTab projectId={id} canWrite={canWrite} />
        </TabsContent>
      </Tabs>

      <TaskDetailDialog
        task={detailTask}
        allTasks={tasks}
        open={Boolean(detailTask)}
        onOpenChange={(o) => { if (!o) setDetailTask(null); }}
        taskStatus={detailTask && statusSummary?.task_status?.[detailTask.id]}
      />
    </div>
  );
}

/**
 * Status summary card (delayed / at-risk / completed-early) extracted as
 * a local subcomponent — heavy JSX but tightly coupled to ProjectDetail.
 */
function StatusSummaryCard({ summary, t }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="status-summary-card">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-4">
        <div>
          <p className="overline-label">{t("activitySummary")}</p>
          <p className="text-xs text-slate-500 mt-0.5">{t("summaryAsOf")} {summary.as_of}</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {summary.counts.delayed > 0 && <span data-testid="badge-delayed" className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">{summary.counts.delayed} {t("delayed")}</span>}
          {summary.counts.at_risk > 0 && <span data-testid="badge-at-risk" className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">{summary.counts.at_risk} {t("atRisk")}</span>}
          {summary.counts.early > 0 && <span data-testid="badge-early" className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{summary.counts.early} {t("early")}</span>}
          <span data-testid="badge-in-progress" className="text-xs font-bold px-2.5 py-1 rounded-full bg-blue-50 text-blue-900 border border-blue-200">{summary.counts.in_progress} {t("inProgress")}</span>
          <span data-testid="badge-completed" className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{summary.counts.completed + summary.counts.early} {t("completed")}</span>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-0">
        <StatusColumn t={t} title={t("delayed")} accent="red" icon={<WarningCircle size={16} weight="fill" className="text-red-500" />} items={summary.delayed} emptyKey="noDelayed" testidPrefix="delayed" render={(it) => (
          <>
            <span className="text-red-600 font-bold">{it.days} {t("daysLate")}</span>
            {" · "}{t("plannedEnd")} {it.end_date} · {it.progress}%
          </>
        )} />
        <StatusColumn t={t} title={t("atRisk")} accent="amber" icon={<ArrowElbowDownRight size={16} weight="bold" className="text-amber-600" />} items={summary.at_risk} emptyKey="noAtRisk" testidPrefix="atrisk" render={(it) => (
          <>
            {t("blockedBy")} <span className="font-medium text-amber-800">«{it.blocker_name}»</span>
            {" — "}<span className="text-amber-700 font-bold">{it.days} {t("daysCarryover")}</span>
          </>
        )} />
        <StatusColumn t={t} title={t("completedEarly")} accent="emerald" icon={<CheckCircle size={16} weight="fill" className="text-emerald-600" />} items={summary.early} emptyKey="noEarly" testidPrefix="early" render={(it) => (
          <>
            <span className="text-emerald-700 font-bold">{it.days} {t("daysAhead")}</span>
            {" · "}{t("plannedEnd")} {it.end_date}
          </>
        )} />
      </div>
      <details className="border-t border-slate-100 group">
        <summary className="px-5 py-2.5 text-xs uppercase tracking-widest font-bold text-slate-500 cursor-pointer hover:bg-slate-50 select-none" data-testid="summary-text-toggle">
          {t("summaryText")} <span className="text-slate-400 font-normal group-open:hidden">— {t("expandDetail")}</span>
        </summary>
        <pre className="px-5 py-4 text-xs text-slate-700 whitespace-pre-wrap font-sans bg-slate-50/60 border-t border-slate-100" data-testid="summary-text-block">{summary.summary_text}</pre>
      </details>
    </div>
  );
}

function StatusColumn({ t, title, accent, icon, items, emptyKey, testidPrefix, render }) {
  const accentText = { red: "text-red-700", amber: "text-amber-800", emerald: "text-emerald-700" }[accent];
  return (
    <div className={`p-5 ${testidPrefix !== "early" ? "border-r border-slate-100" : ""}`}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <p className={`text-xs font-bold uppercase tracking-wider ${accentText}`}>{title}</p>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid={`${testidPrefix}-empty`}>{t(emptyKey)}</p>
      ) : (
        <ul className="space-y-2.5" data-testid={`${testidPrefix}-list`}>
          {items.map((it) => (
            <li key={it.id} data-testid={`${testidPrefix}-item-${it.id}`} className="text-xs">
              <p className="font-semibold text-slate-900 leading-tight">{it.name}</p>
              <p className="text-slate-500 mt-0.5">{render(it)}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">{it.phase_label}</p>
              {it.last_update && (
                <p className="text-[10px] text-slate-500 mt-1 leading-snug" data-testid={testidPrefix === "delayed" ? `last-update-${it.id}` : undefined}>
                  <span className="font-semibold">{t("progressReported")}:</span>{" "}
                  {typeof it.last_update.delta === "number" ? (
                    <span
                      data-testid={testidPrefix === "delayed" ? `delta-${it.id}` : undefined}
                      className={it.last_update.delta > 0 ? "text-emerald-700 font-bold" : it.last_update.delta < 0 ? "text-red-600 font-bold" : "text-slate-500 font-bold"}
                    >
                      {it.last_update.delta > 0 ? "+" : ""}{it.last_update.delta}%
                    </span>
                  ) : (
                    <span className="text-slate-500 font-bold">{it.last_update.progress}%</span>
                  )}
                  {typeof it.last_update.previous_progress === "number" && (
                    <span className="text-slate-400"> ({it.last_update.previous_progress}% → {it.last_update.progress}%)</span>
                  )}
                  <span className="text-slate-400"> · {(it.last_update.updated_at || "").slice(0, 10)}{testidPrefix === "delayed" ? ` · ${it.last_update.updated_by}` : ""}</span>
                  {it.last_update.note && <span className="block italic text-slate-400 mt-0.5">“{it.last_update.note}”</span>}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
